import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, ApiError, formatDateTime } from '../lib/api';
import type { ApostilleVerifyResult } from '../lib/types';
import { ErrorNote, StatusBadge } from '../ui/components';

type Lang = 'en' | 'fr' | 'crs';

const LANG_LABEL: Record<Lang, string> = { en: 'English', fr: 'Français', crs: 'Kreol Seselwa' };

const COPY: Record<Lang, { title: string; blurb: string; placeholder: string; button: string; busy: string; notFound: string; back: string }> = {
  en: {
    title: 'Verify an e-Apostille',
    blurb: 'Check whether a Seychelles e-Apostille reference is genuine and still valid. No sign-in required.',
    placeholder: 'e-Apostille number or DocVault serial (e.g. APO-2026-SEED001)',
    button: 'Verify',
    busy: 'Checking…',
    notFound: 'Enter a reference above and press Verify.',
    back: 'Foreign Affairs & Diaspora staff sign-in',
  },
  fr: {
    title: 'Vérifier une e-Apostille',
    blurb: "Vérifiez si une référence d'e-Apostille des Seychelles est authentique et toujours valide. Aucune connexion requise.",
    placeholder: "Numéro d'e-Apostille ou numéro de série DocVault (ex. APO-2026-SEED001)",
    button: 'Vérifier',
    busy: 'Vérification…',
    notFound: 'Saisissez une référence ci-dessus et appuyez sur Vérifier.',
    back: 'Connexion du personnel — Affaires étrangères & Diaspora',
  },
  crs: {
    title: 'Verifye en e-Apostille',
    blurb: 'Verifye si en referans e-Apostille Sesel i vre e i touzour valid. Ou pa bezwen konnekte.',
    placeholder: 'Nimero e-Apostille oubyen nimero DocVault (par egzanp APO-2026-SEED001)',
    button: 'Verifye',
    busy: 'I pe verifye…',
    notFound: 'Antre en referans anho e aport lo Verifye.',
    back: 'Konneksyon staff — Zafer Etranzer & Dyaspora',
  },
};

/**
 * Genuinely public page — no auth, no persona required. Calls M42's own
 * public verify endpoint directly (GET /api/v1/m42/apostille/verify/:ref,
 * mounted with `security: []` ahead of the auth middleware in
 * packages/modules/m42-foreign-affairs/src/app.ts, rate-limited to 30/min/IP
 * on the backend). Reachable at /verify regardless of sign-in state — see
 * App.tsx, where this route is declared outside the persona gate.
 */
export function VerifyApostille() {
  const [params] = useSearchParams();
  const [ref, setRef] = useState(params.get('ref') ?? '');
  const [lang, setLang] = useState<Lang>((params.get('lang') as Lang) ?? 'en');
  const [result, setResult] = useState<ApostilleVerifyResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const t = COPY[lang];

  const verify = async () => {
    const trimmed = ref.trim();
    if (!trimmed) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const res = await api.get<ApostilleVerifyResult>(`/api/v1/m42/apostille/verify/${encodeURIComponent(trimmed)}?lang=${lang}`);
      setResult(res);
    } catch (e) {
      setError(e instanceof ApiError ? (e.status === 429 ? e.message : `${e.message} (${e.code})`) : 'Could not reach the verification service.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink-950">
      <div className="mx-auto max-w-xl px-6 py-16">
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-600 text-sm font-black text-white">SC</div>
            <div className="text-sm font-bold text-white">One Gov Seychelles</div>
          </div>
          <select
            className="rounded-lg border border-ink-700 bg-ink-900 px-2.5 py-1.5 text-xs font-semibold text-slate-200"
            value={lang}
            onChange={(e) => setLang(e.target.value as Lang)}
          >
            {(Object.keys(LANG_LABEL) as Lang[]).map((l) => (
              <option key={l} value={l}>
                {LANG_LABEL[l]}
              </option>
            ))}
          </select>
        </div>

        <div className="card p-6">
          <h1 className="text-xl font-bold text-slate-900">{t.title}</h1>
          <p className="mt-1 text-sm text-slate-500">{t.blurb}</p>

          <div className="mt-5 flex gap-2">
            <input
              className="field-input"
              value={ref}
              onChange={(e) => setRef(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && verify()}
              placeholder={t.placeholder}
              autoFocus
            />
            <button className="btn-primary shrink-0" onClick={verify} disabled={busy || ref.trim().length === 0}>
              {busy ? t.busy : t.button}
            </button>
          </div>

          {error ? (
            <div className="mt-4">
              <ErrorNote message={error} />
            </div>
          ) : null}

          {result ? (
            <div
              className={`mt-5 rounded-lg border p-4 ${
                result.valid ? 'border-emerald-200 bg-emerald-50' : 'border-rose-200 bg-rose-50'
              }`}
            >
              <div className="flex items-center gap-2">
                <StatusBadge status={result.messageKey} />
              </div>
              <p className={`mt-2 text-sm font-medium ${result.valid ? 'text-emerald-800' : 'text-rose-800'}`}>{result.message}</p>
              {result.valid ? (
                <dl className="mt-3 space-y-1 text-sm text-slate-700">
                  {result.apostilleNo ? (
                    <div className="flex justify-between gap-3">
                      <dt className="text-slate-500">e-Apostille no.</dt>
                      <dd className="font-mono font-semibold">{result.apostilleNo}</dd>
                    </div>
                  ) : null}
                  {result.documentType ? (
                    <div className="flex justify-between gap-3">
                      <dt className="text-slate-500">Document type</dt>
                      <dd className="font-medium">{result.documentType.replaceAll('_', ' ')}</dd>
                    </div>
                  ) : null}
                  {result.destinationCountry ? (
                    <div className="flex justify-between gap-3">
                      <dt className="text-slate-500">Destination country</dt>
                      <dd className="font-medium">{result.destinationCountry}</dd>
                    </div>
                  ) : null}
                  {result.issuedAt ? (
                    <div className="flex justify-between gap-3">
                      <dt className="text-slate-500">Issued</dt>
                      <dd className="font-medium">{formatDateTime(result.issuedAt)}</dd>
                    </div>
                  ) : null}
                </dl>
              ) : null}
            </div>
          ) : !error ? (
            <p className="mt-4 text-xs text-slate-400">{t.notFound}</p>
          ) : null}
        </div>

        <div className="mt-6 text-center">
          <a href="/" className="text-xs font-semibold text-slate-500 underline hover:text-slate-300">
            {t.back}
          </a>
        </div>
      </div>
    </div>
  );
}
